import type { Hit } from 'instantsearch.js';
import { useContext, useEffect, useState } from 'react';
import { useHits, useInstantSearch } from 'react-instantsearch-core';
import { FormattedMessage } from 'react-intl';

import { getStrapSizeDatas } from '@/utils/straps';

import { ProductListingCard } from '@/components/ProductListingCard';
import { Typography } from '@/components/Typography';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useListingState } from '@/hooks/useListingState';
import { useSendAlgoliaEvent } from '@/hooks/useSendAlgoliaEvent';
import { isEyewearHit } from '@/integrations/algolia/helpers';
import type {
  EyewearHit,
  StrapHit,
  WatchHit,
} from '@/integrations/algolia/types';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import type {
  ProductCatalogModeCtaStyle,
  WithChildren,
  WithHTMLProp,
} from '@/lib/types';
import { commonMessages } from '@/messages/common';
import { useAlgoliaEvent } from '@/providers/AlgoliaEventProvider';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';

import * as S from './ListingHits.styles';

export interface ListingHitsProps
  extends WithChildren<false>, WithHTMLProp<'className'> {
  hideFavorite?: boolean;
  isMostRelevantCta?: boolean;
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  productType?: string;
  showNotFound?: boolean;
}

export const ListingHits = ({
  children,
  className,
  hideFavorite = false,
  isMostRelevantCta = false,
  productCatalogModeCtaStyle = 'HIDDEN',
  productType,
  showNotFound = true,
}: ListingHitsProps) => {
  const { status } = useInstantSearch();
  const { hits, results } = useHits<WatchHit | StrapHit | EyewearHit>();
  const { sendViewItemListEvent } = useGoogleAnalytics();
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const queryID = results?.queryID ?? '';
  const positionOffset = (results?.page ?? 0) * (results?.hitsPerPage ?? 0);
  const [strapSizeDatas, setStrapSizeDatas] = useState<Record<string, any>>({});
  const { indexName, setIndexName, setIsSearched, setQueryID } =
    useAlgoliaEvent();
  const { listingType } = useListingState();

  // Determine the product type for grid layout
  let detectedProductType: string;

  // Special case: If we're in STRAP mode, NEVER use eyewear layout
  // regardless of what props were passed
  if (listingType === 'STRAP') {
    detectedProductType = 'watch';
  }
  // Otherwise determine the product type based on priority:
  else if (productType) {
    // 1. If productType is explicitly passed, use it
    detectedProductType = productType;
  } else if (listingType) {
    // 2. If listingType is available from global state
    detectedProductType = listingType === 'EYEWEAR' ? 'eyewear' : 'watch';
  } else if (hits.length > 0) {
    // 3. Fallback to hit detection
    detectedProductType = isEyewearHit(hits[0]) ? 'eyewear' : 'watch';
  } else {
    // Default fallback
    detectedProductType = 'watch';
  }
  const draftMode = useContext(DraftModeContext);

  useEffect(() => {
    if (hits.length > 0) {
      const skus = hits.map((hit) => hit.objectID);

      sendViewItemListEvent({
        skus,
        itemListName: 'Proudct listing',
      });

      setIsSearched(true);
      setQueryID(queryID);
      setIndexName(results?.index ?? '');
    }
  }, [hits]);

  useEffect(() => {
    const fetchStrapSizes = async () => {
      const strapHits = hits.filter(
        (hit): hit is Hit<StrapHit> => 'buckle_type' in hit,
      );
      const sizeDatas = await getStrapSizeDatas(
        strapHits,
        indexName,
        draftMode,
      );
      setStrapSizeDatas(sizeDatas);
    };
    if (hits && hits.length > 0 && indexName) {
      fetchStrapSizes();
    }
  }, [hits, indexName]);

  const noResults =
    showNotFound &&
    !results?.nbHits &&
    status === 'idle' &&
    results &&
    results.queryID;

  if (noResults) {
    return (
      <Typography
        data-test-id='ListingHitsNoResultsFound'
        fontFamily='italianPlate'
        m={6}
        scale='h3'
        textAlign='center'
      >
        <FormattedMessage {...commonMessages.noResultsFound} />
      </Typography>
    );
  }

  return (
    <S.ProductsGrid
      $productType={detectedProductType}
      className={className}
      data-test-id='ListingHitsWatchesGrid'
      gridGap={2}
    >
      {hits?.map((hit, index) => (
        <ProductListingCard
          key={hit.objectID}
          cardClickHandler={() => {
            // Algolia rejects clickedObjectIDsAfterSearch with an empty queryID (422).
            if (!queryID) {
              sendAlgoliaEvent({
                event: AlgoliaEventsNames.clickedObjectIDs,
                objectIDs: [hit.objectID],
                indexName: results?.index,
              });
              return;
            }
            sendAlgoliaEvent({
              event: AlgoliaEventsNames.clickedObjectIDsAfterSearch,
              objectIDs: [hit.objectID],
              queryID: queryID,
              indexName: results?.index,
              positions: [positionOffset + index + 1],
            });
          }}
          data-test-id={`ListingHitsProductListingCard-${index}`}
          hideFavorite={hideFavorite}
          hit={hit}
          index={index}
          isMostRelevantCta={isMostRelevantCta}
          productCatalogModeCtaStyle={productCatalogModeCtaStyle}
          strapSizeDatas={strapSizeDatas[hit.objectID]}
        />
      ))}
      {children}
    </S.ProductsGrid>
  );
};
